import React, { useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Download } from '@mui/icons-material';

import {
  IDatevExportRequest,
  IDatevExportResult,
  useExportDatev,
} from '../hooks/reactQuerys/InvoiceQueries';

/** Vorbelegung je Kontenrahmen — dieselben Werte wie `datev.KONTEN_VORGABE`. */
const KONTEN_VORGABE: Record<
  IDatevExportRequest['Kontenrahmen'],
  Pick<IDatevExportRequest, 'Erloeskonto' | 'Debitorenkonto' | 'Bankkonto'>
> = {
  '03': { Erloeskonto: '8400', Debitorenkonto: '10000', Bankkonto: '1200' },
  '04': { Erloeskonto: '4400', Debitorenkonto: '10000', Bankkonto: '1800' },
};

/**
 * Was sich das Büro merken soll. Der Zeitraum gehört nicht dazu — der ist
 * jedes Mal ein anderer. Nur in diesem Browser; die Angaben sind keine
 * Geheimnisse, aber auch nichts, das jeder Arbeitsplatz neu tippen sollte.
 */
const STORAGE_KEY = 'dino.datev.export';

const toIso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;

/** Der Vormonat: der übliche Zeitraum für die Buchhaltung. */
const vormonat = (): Pick<IDatevExportRequest, 'von' | 'bis'> => {
  const heute = new Date();
  const erster = new Date(heute.getFullYear(), heute.getMonth() - 1, 1);
  const letzter = new Date(heute.getFullYear(), heute.getMonth(), 0);
  return { von: toIso(erster), bis: toIso(letzter) };
};

/**
 * Beginn des Wirtschaftsjahrs, in dem `von` liegt. Gemerkt werden Monat und
 * Tag (ein abweichendes Wirtschaftsjahr ab 1. Juli bleibt eines), das Jahr
 * folgt dem Zeitraum — sonst stünde im Januar noch das Vorjahr drin.
 */
export const wirtschaftsjahrFuer = (von: string, gemerkt?: string): string => {
  const monatTag = /^\d{4}-(\d{2}-\d{2})$/.exec(gemerkt ?? '')?.[1] ?? '01-01';
  const jahr = Number(von.slice(0, 4));
  const kandidat = `${jahr}-${monatTag}`;

  return kandidat <= von ? kandidat : `${jahr - 1}-${monatTag}`;
};

const ladeGemerkt = (): Partial<IDatevExportRequest> => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
  } catch {
    return {};
  }
};

const merke = (request: IDatevExportRequest) => {
  try {
    const { von: _von, bis: _bis, ...rest } = request;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(rest));
  } catch {
    /* Speicher nicht verfügbar — dann eben beim nächsten Mal neu tippen. */
  }
};

const herunterladen = (result: IDatevExportResult) => {
  const binary = atob(result.Data);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  const url = URL.createObjectURL(new Blob([bytes], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = result.FileName;
  link.click();
  URL.revokeObjectURL(url);
};

const euro = (wert: number) =>
  wert.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

/** Was der Chat vorgeben kann — alles andere kommt aus dem Gemerkten. */
export type IDatevExportVorbelegung = Pick<
  IDatevExportRequest,
  'von' | 'bis' | 'mitZahlungen'
>;

/**
 * Knopf und Dialog in der Rechnungsübersicht. Der Dialog wird erst beim
 * Öffnen eingehängt, damit er jedes Mal mit frischem Zeitraum beginnt.
 */
export const InvoiceDatevExportDialog: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Button
        variant='outlined'
        sx={{ float: 'right', mt: 1 }}
        onClick={() => setIsOpen(true)}
      >
        DATEV-Export
      </Button>
      {isOpen && <DatevExportDialog onClose={() => setIsOpen(false)} />}
    </>
  );
};

/**
 * DATEV-Export der Rechnungen (#1336) — der erste, **bewusst einfache**
 * Schritt: Rechnungen entstehen heute in ArnoTop, sollen künftig in DiNo
 * entstehen, und bis dahin genügt ein Stapel, den DATEV einlesen kann.
 * Jede Rechnung geht mit ihrem Bruttobetrag auf **ein** Erlöskonto; das steht
 * im Dialog, damit es niemand für eine fertige Buchhaltung hält.
 *
 * Derselbe Dialog öffnet sich aus dem Chat, mit `vorbelegung` — eine Stelle
 * für Konten und Prüfung, nicht zwei.
 */
export const DatevExportDialog: React.FC<{
  onClose: () => void;
  vorbelegung?: IDatevExportVorbelegung;
}> = ({ onClose, vorbelegung }) => {
  const { exportDatev, isPendingExportDatev } = useExportDatev();
  const [request, setRequest] = useState<IDatevExportRequest>(() => {
    const gemerkt = ladeGemerkt();
    const zeitraum = vorbelegung ?? vormonat();
    const kontenrahmen = gemerkt.Kontenrahmen ?? '03';

    return {
      Beraternummer: '',
      Mandantennummer: '',
      Sachkontenlaenge: 4,
      mitZahlungen: true,
      ...KONTEN_VORGABE[kontenrahmen],
      ...gemerkt,
      Kontenrahmen: kontenrahmen,
      WirtschaftsjahrBeginn: wirtschaftsjahrFuer(
        zeitraum.von,
        gemerkt.WirtschaftsjahrBeginn
      ),
      ...zeitraum,
    };
  });
  const [result, setResult] = useState<IDatevExportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof IDatevExportRequest>(
    key: K,
    value: IDatevExportRequest[K]
  ) => setRequest(current => ({ ...current, [key]: value }));

  async function handleExport() {
    setError(null);
    setResult(null);

    try {
      const antwort = await exportDatev(request);
      merke(request);
      setResult(antwort);

      if (antwort.AnzahlRechnungen + antwort.AnzahlZahlungen > 0) {
        herunterladen(antwort);
      }
    } catch (exception) {
      // Der Server nennt, welche Angabe nicht zum DATEV-Format passt.
      const detail =
        exception instanceof Error && exception.message
          ? exception.message.replace(/^\d{3} [^:]+: /, '')
          : '';
      setError(detail !== '' ? detail : 'Der Export ist gescheitert.');
    }
  }

  return (
    <>
      <Dialog open onClose={onClose} maxWidth='sm' fullWidth>
        <DialogTitle>DATEV-Export (Buchungsstapel)</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity='info'>
              Vereinfachter Stapel: jede Rechnung mit ihrem Bruttobetrag auf ein
              Erlöskonto, alle auf einen Sammeldebitor. Gebühren und
              durchlaufende Posten werden noch nicht getrennt — bitte mit dem
              Steuerberater abstimmen, bevor der Stapel gebucht wird. Stornierte
              Rechnungen fehlen.
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label='Von'
                  type='date'
                  size='small'
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={request.von}
                  onChange={event => set('von', event.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label='Bis'
                  type='date'
                  size='small'
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={request.bis}
                  onChange={event => set('bis', event.target.value)}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label='Beraternummer'
                  size='small'
                  fullWidth
                  value={request.Beraternummer}
                  onChange={event =>
                    set('Beraternummer', event.target.value.trim())
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label='Mandantennummer'
                  size='small'
                  fullWidth
                  value={request.Mandantennummer}
                  onChange={event =>
                    set('Mandantennummer', event.target.value.trim())
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label='Wirtschaftsjahr ab'
                  type='date'
                  size='small'
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={request.WirtschaftsjahrBeginn}
                  onChange={event =>
                    set('WirtschaftsjahrBeginn', event.target.value)
                  }
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  label='Kontenrahmen'
                  select
                  size='small'
                  fullWidth
                  value={request.Kontenrahmen}
                  onChange={event => {
                    const skr = event.target
                      .value as IDatevExportRequest['Kontenrahmen'];
                    // Der Kontenrahmen bestimmt die Kontennummern — die
                    // Vorgaben des anderen wären hier falsch.
                    setRequest(current => ({
                      ...current,
                      Kontenrahmen: skr,
                      ...KONTEN_VORGABE[skr],
                    }));
                  }}
                >
                  <MenuItem value='03'>SKR 03</MenuItem>
                  <MenuItem value='04'>SKR 04</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={3}>
                <TextField
                  label='Sachkontenlänge'
                  type='number'
                  size='small'
                  fullWidth
                  value={request.Sachkontenlaenge}
                  onChange={event =>
                    set('Sachkontenlaenge', Number(event.target.value))
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label='Erlöskonto'
                  size='small'
                  fullWidth
                  value={request.Erloeskonto}
                  onChange={event =>
                    set('Erloeskonto', event.target.value.trim())
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label='Debitor (Sammelkonto)'
                  size='small'
                  fullWidth
                  value={request.Debitorenkonto}
                  onChange={event =>
                    set('Debitorenkonto', event.target.value.trim())
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label='Bankkonto'
                  size='small'
                  fullWidth
                  disabled={!request.mitZahlungen}
                  value={request.Bankkonto}
                  onChange={event =>
                    set('Bankkonto', event.target.value.trim())
                  }
                />
              </Grid>
            </Grid>

            <FormControlLabel
              control={
                <Checkbox
                  checked={request.mitZahlungen}
                  onChange={event => set('mitZahlungen', event.target.checked)}
                />
              }
              label='Zahlungseingänge des Zeitraums mit exportieren'
            />

            {error !== null && <Alert severity='error'>{error}</Alert>}

            {result !== null && (
              <Alert
                severity={
                  result.AnzahlRechnungen + result.AnzahlZahlungen > 0
                    ? 'success'
                    : 'info'
                }
              >
                {result.AnzahlRechnungen + result.AnzahlZahlungen > 0 ? (
                  <>
                    <Typography variant='body2'>
                      {result.FileName} wurde heruntergeladen:{' '}
                      {result.AnzahlRechnungen} Rechnungen (
                      {euro(result.SummeRechnungen)})
                      {request.mitZahlungen &&
                        `, ${result.AnzahlZahlungen} Zahlungen (${euro(
                          result.SummeZahlungen
                        )})`}
                      .
                    </Typography>
                  </>
                ) : (
                  'Im Zeitraum gibt es keine Rechnungen oder Zahlungen.'
                )}
                {result.Hinweise.length > 0 && (
                  <Stack component='ul' sx={{ m: 0, mt: 1, pl: 2 }}>
                    {result.Hinweise.map(hinweis => (
                      <Typography
                        component='li'
                        variant='caption'
                        key={hinweis}
                      >
                        {hinweis}
                      </Typography>
                    ))}
                  </Stack>
                )}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color='inherit' onClick={onClose}>
            Schließen
          </Button>
          <Button
            variant='contained'
            startIcon={
              isPendingExportDatev ? (
                <CircularProgress size={16} />
              ) : (
                <Download />
              )
            }
            disabled={
              isPendingExportDatev ||
              request.Beraternummer === '' ||
              request.Mandantennummer === ''
            }
            onClick={() => void handleExport()}
          >
            Exportieren
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
